// src/components/padrekko/qc/CaseNotesPanel.jsx
//
// Day 8 Phase A — case-level notes. Mounted in QcSummary between
// RunRollupSection and PerPadSection (Flag A1).
//
// Differs from FindingNotesSection:
//   - Always visible (no "+ Add note" toggle). Compose box always shown.
//   - Empty-state placeholder copy when there are no notes yet.
//   - Posts fire case_note_added SSE fanout — participants see it in the
//     collab rail + get an unread badge bump.
//
// Per-note rendering mirrors FindingNotesSection's NoteRow (deliberately
// kept structurally parallel for visual coherence). Sort: ascending.

import { useState } from "react";
import { MessageSquare, Loader2, Pencil, Trash2, X as XIcon, AlertCircle, Check } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { useAuthStore } from "../../../stores/useAuthStore";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";

const NOTE_SOFT_MAX = 2000;

function formatRelativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

function authorLabel(note) {
  const name = note.author_display ?? "Former member";
  // Server-resolved: snapshotted at write time, falling back to a join
  // for notes written before migration 042.
  const org = note.author_org_display || "Unknown org";
  return `${name} · ${org}`;
}

/* ── Single note row ─────────────────────────────────────────────────── */
function NoteRow({ note, currentUserId, isServiceProvider, onEdit, onDelete, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [error, setError] = useState(null);
  const editInflightId = useQcStore((s) => s.editNoteInflightId);
  const deleteInflightId = useQcStore((s) => s.deleteNoteInflightId);
  const isSavingEdit = editInflightId === note.id;
  const isDeleting = deleteInflightId === note.id;

  const isAuthor = note.author_user_id === currentUserId;
  // readOnly (case complete) locks edit/delete to match the backend's
  // 403-on-write-at-complete; otherwise author or a service-provider may edit.
  const canMutate = (isAuthor || isServiceProvider) && !readOnly;

  // `edited` is SERVER-DERIVED, not counted from edit_history. The drafts are
  // withheld from other orgs (the author's working-out), and computing this
  // from an array that is deliberately absent reported every edited note as
  // never edited — an absence turned into a false statement about the record.
  const wasEdited = note.edited === true;

  async function handleSaveEdit() {
    setError(null);
    const result = await onEdit(note.id, draft);
    if (result.ok) {
      setEditing(false);
    } else {
      setError(result.error || "Failed to save");
    }
  }
  async function handleDelete() {
    setError(null);
    const result = await onDelete(note.id, note.case_id);
    if (!result.ok) {
      setError(result.error || "Failed to delete");
    }
  }

  return (
    <div className="px-2.5 py-2 rounded border border-slate-800/60 bg-slate-900/30">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] text-slate-400 leading-snug min-w-0">
          <span className="text-slate-200 font-medium">{authorLabel(note)}</span>
          <span className="text-fg-secondary mx-1">·</span>
          <span className="text-fg-secondary" title={note.created_at}>
            {formatRelativeTime(note.created_at)}
          </span>
          {wasEdited && (
            <>
              <span className="text-fg-secondary mx-1">·</span>
              <span className="text-fg-secondary italic" title={`Edited ${formatRelativeTime(note.updated_at)}`}>
                edited
              </span>
            </>
          )}
        </div>
        {canMutate && !editing && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => { setDraft(note.body); setEditing(true); setError(null); }}
              disabled={isSavingEdit || isDeleting}
              className="p-0.5 rounded text-fg-secondary hover:text-slate-200 transition
                         disabled:opacity-40 disabled:cursor-not-allowed"
              title="Edit"
            >
              <Pencil size={10} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isSavingEdit || isDeleting}
              className="p-0.5 rounded text-fg-secondary hover:text-red-300 transition
                         disabled:opacity-40 disabled:cursor-not-allowed"
              title="Delete"
            >
              {isDeleting
                ? <Loader2 size={10} className="animate-spin" />
                : <Trash2 size={10} />}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="text-[11px] text-slate-100 leading-relaxed mt-1 whitespace-pre-wrap break-words">
          {note.body}
        </div>
      ) : (
        <div className="mt-1 space-y-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={NOTE_SOFT_MAX}
            rows={3}
            disabled={isSavingEdit}
            autoFocus
            className="w-full text-[11px] px-2 py-1.5 rounded border
                       border-slate-700 bg-slate-900 text-slate-100
                       focus:outline-none focus:border-slate-500
                       disabled:opacity-50 leading-snug resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[9px] text-fg-secondary tabular-nums">
              {draft.length}/{NOTE_SOFT_MAX}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { setEditing(false); setError(null); }}
                disabled={isSavingEdit}
                className="text-[10px] px-2 py-0.5 rounded border
                           border-slate-700 text-slate-400 hover:bg-slate-800
                           disabled:opacity-40 transition"
              >
                <XIcon size={9} className="inline" /> Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSavingEdit || !draft.trim() || draft === note.body}
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border
                           bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                           hover:bg-[#16f2d4]/25
                           disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {isSavingEdit
                  ? <Loader2 size={9} className="animate-spin" />
                  : <Check size={9} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug mt-1">
          <AlertCircle size={10} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/* ── Panel ───────────────────────────────────────────────────────────── */
export default function CaseNotesPanel({ caseId }) {
  const notesByCase = useQcStore((s) => s.notesByCase);
  const addCaseNote = useQcStore((s) => s.addCaseNote);
  const editNote = useQcStore((s) => s.editNote);
  const deleteNote = useQcStore((s) => s.deleteNote);
  const caseNoteInflight = useQcStore((s) => s.caseNoteInflight);
  // Phase 6 — close lockdown: at 'complete' the notes UI is read-only
  // (compose hidden, edit/delete suppressed) to match the backend 403.
  const caseComplete = useQcStore((s) => s.caseInfo?.current_stage === "complete");

  const user = useAuthStore((s) => s.user);
  const currentUserId = user?.id ?? null;
  const effectiveOrg = useEffectiveOrg();
  const isServiceProvider = effectiveOrg?.is_service_provider === true;

  const notes = notesByCase.get(caseId)?.case ?? [];
  const count = notes.length;

  const [draft, setDraft] = useState("");
  const [inlineError, setInlineError] = useState(null);

  async function handleSubmit() {
    setInlineError(null);
    const result = await addCaseNote(caseId, draft);
    if (result.ok) {
      setDraft("");
    } else {
      setInlineError(result.error || "Failed to add note");
    }
  }

  if (!caseId) return null;

  return (
    <div className="px-4 py-3 space-y-2 border-b border-slate-800/80">
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary
                      flex items-center gap-1.5">
        <MessageSquare size={10} />
        Case notes · {count}
      </div>

      {count === 0 && (
        <div className="text-[10px] text-fg-secondary italic leading-snug">
          No notes yet. Add the first note to capture case context.
        </div>
      )}

      {notes.map((n) => (
        <NoteRow
          key={n.id}
          note={n}
          currentUserId={currentUserId}
          isServiceProvider={isServiceProvider}
          onEdit={editNote}
          onDelete={deleteNote}
          readOnly={caseComplete}
        />
      ))}

      {/* Compose box — always visible while the case is open (Flag spec).
          Suppressed at 'complete' (close lockdown); a read-only hint replaces
          it so the section doesn't look broken. */}
      {caseComplete ? (
        <div className="text-[10px] text-fg-secondary italic leading-snug pt-1">
          Case closed — notes are read-only.
        </div>
      ) : (
      <div className="space-y-1 pt-1">
        <textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (inlineError) setInlineError(null); }}
          maxLength={NOTE_SOFT_MAX}
          rows={2}
          disabled={caseNoteInflight}
          placeholder="Add a case-level note…"
          className="w-full text-[11px] px-2 py-1.5 rounded border
                     border-slate-700 bg-slate-900 text-slate-100
                     placeholder:text-slate-600
                     focus:outline-none focus:border-slate-500
                     disabled:opacity-50 leading-snug resize-none"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9px] text-fg-secondary tabular-nums">
            {draft.length}/{NOTE_SOFT_MAX}
          </span>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={caseNoteInflight || !draft.trim()}
            className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded border
                       bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                       hover:bg-[#16f2d4]/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {caseNoteInflight && <Loader2 size={10} className="animate-spin" />}
            {caseNoteInflight ? "Adding…" : "Add note"}
          </button>
        </div>
        {inlineError && (
          <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug">
            <AlertCircle size={10} className="mt-0.5 shrink-0" />
            <span>{inlineError}</span>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
